// Staff / Moderation Console — reports + DMCA
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import DashboardQuickActions from "../components/DashboardQuickActions";
import { Shield, AlertTriangle, FileWarning, Check, X, Ban, DollarSign } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function StaffConsole() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [reports, setReports] = useState([]);
  const [dmca, setDmca] = useState([]);
  const [tab, setTab] = useState("reports");

  const load = () => {
    api.get("/staff/overview").then(({ data }) => setOverview(data));
    api.get("/staff/reports").then(({ data }) => setReports(data));
    api.get("/staff/dmca").then(({ data }) => setDmca(data));
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return <div className="p-20 text-center text-white/40">Sign in as staff or owner.</div>;
  if (user.role !== "staff" && user.role !== "owner") {
    return (
      <div className="p-20 text-center space-y-3">
        <Shield className="w-12 h-12 mx-auto text-nexus-crimson" />
        <h2 className="font-display text-2xl">Staff access required</h2>
        <p className="text-white/50">Sign in as staff@nexus.io (Moderator).</p>
      </div>
    );
  }

  const actionReport = async (r, action) => {
    await api.post(`/staff/reports/${r.id}/action`, { action });
    toast.success(`Report ${action}`);
    load();
  };
  const actionDmca = async (d, action) => {
    await api.post(`/staff/dmca/${d.id}/action`, { action });
    toast.success(`DMCA ${action}`);
    load();
  };

  return (
    <div className="space-y-10" data-testid="staff-console">
      <Toaster theme="dark" position="top-center" />
      <div>
        <div className="label-eyebrow mb-2 text-nexus-crimson">// staff / moderation console</div>
        <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter">Hold the line.</h1>
        <DashboardQuickActions className="mt-3" />
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/5">
          <div className="bg-nexus-panel border-l-2 border-nexus-crimson p-5"><div className="label-eyebrow text-nexus-crimson">// open reports</div><div className="font-display font-black text-3xl mt-2">{overview.open_reports}</div></div>
          <div className="bg-nexus-surface p-5"><div className="label-eyebrow">// actioned</div><div className="font-display font-black text-3xl mt-2 text-nexus-mint">{overview.actioned}</div></div>
          <div className="bg-nexus-surface p-5"><div className="label-eyebrow">// dismissed</div><div className="font-display font-black text-3xl mt-2 text-white/50">{overview.dismissed}</div></div>
          <div className="bg-nexus-panel border-l-2 border-nexus-amber p-5"><div className="label-eyebrow text-nexus-amber">// open dmca</div><div className="font-display font-black text-3xl mt-2">{overview.open_dmca}</div></div>
          <div className="bg-nexus-surface p-5"><div className="label-eyebrow">// takedowns</div><div className="font-display font-black text-3xl mt-2">{overview.takedowns}</div></div>
        </div>
      )}

      <div className="flex gap-2 border-b border-white/10">
        <button onClick={() => setTab("reports")} data-testid="tab-reports" className={`px-4 py-2 text-[10px] tracking-[0.3em] uppercase font-display flex items-center gap-2 ${tab === "reports" ? "text-nexus-cyan border-b-2 border-nexus-cyan" : "text-white/40"}`}><AlertTriangle className="w-3.5 h-3.5" />Reports</button>
        <button onClick={() => setTab("dmca")} data-testid="tab-dmca" className={`px-4 py-2 text-[10px] tracking-[0.3em] uppercase font-display flex items-center gap-2 ${tab === "dmca" ? "text-nexus-cyan border-b-2 border-nexus-cyan" : "text-white/40"}`}><FileWarning className="w-3.5 h-3.5" />DMCA</button>
        <button onClick={() => setTab("payouts")} data-testid="tab-payouts" className={`px-4 py-2 text-[10px] tracking-[0.3em] uppercase font-display flex items-center gap-2 ${tab === "payouts" ? "text-nexus-cyan border-b-2 border-nexus-cyan" : "text-white/40"}`}><DollarSign className="w-3.5 h-3.5" />Payouts</button>
      </div>

      {tab === "reports" && (
        <div className="space-y-px bg-white/5" data-testid="reports-list">
          {reports.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No reports.</div>}
          {reports.map((r) => (
            <div key={r.id} className="bg-nexus-surface p-5 grid md:grid-cols-6 gap-4 items-center">
              <div className="md:col-span-2">
                <div className="text-sm font-medium">{r.reason}</div>
                <div className="text-[11px] text-white/40 tracking-wider">{r.target_type} · {r.target_id.slice(0, 12)}…</div>
              </div>
              <div className="md:col-span-2 text-xs text-white/60">{r.details || "—"}</div>
              <div className="text-[11px] text-white/40">by {r.reporter_name}</div>
              <div className="flex gap-2 justify-end">
                {r.status === "open" ? (
                  <>
                    <button onClick={() => actionReport(r, "warn")} className="btn-ghost !py-1.5 !px-3 text-[9px]" data-testid={`warn-${r.id}`}>Warn</button>
                    <button onClick={() => actionReport(r, "remove")} className="btn-ghost !py-1.5 !px-3 text-[9px] !border-nexus-crimson !text-nexus-crimson"><Ban className="w-3 h-3" /></button>
                    <button onClick={() => actionReport(r, "dismiss")} className="btn-ghost !py-1.5 !px-3 text-[9px]"><X className="w-3 h-3" /></button>
                  </>
                ) : (
                  <div className="text-[10px] tracking-[0.3em] uppercase text-nexus-mint flex items-center gap-1"><Check className="w-3 h-3" />{r.status}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "dmca" && (
        <div className="space-y-px bg-white/5" data-testid="dmca-list">
          {dmca.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No DMCA notices.</div>}
          {dmca.map((d) => (
            <div key={d.id} className="bg-nexus-surface p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-sm font-medium">{d.claimant_name}</div>
                  <div className="text-[11px] text-white/40">{d.claimant_email} · target: {d.target_type}/{d.target_id.slice(0, 14)}…</div>
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: d.status === "open" ? "#FFC107" : "#00FF66" }}>{d.status}</div>
              </div>
              <div className="text-xs text-white/70 mt-3 italic">&ldquo;{d.work_description}&rdquo;</div>
              {d.status === "open" && (
                <div className="flex gap-2 mt-4">
                  <button onClick={() => actionDmca(d, "takedown")} className="btn-nexus !py-1.5 !px-3 text-[9px]">Issue Takedown</button>
                  <button onClick={() => actionDmca(d, "dismiss")} className="btn-ghost !py-1.5 !px-3 text-[9px]">Dismiss</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {tab === "payouts" && <PayoutsTab />}
    </div>
  );
}

/* ============================== PAYOUTS — release suspended/held NX to PayPal ============================== */
function PayoutsTab() {
  const [users, setUsers] = useState([]);
  const [releases, setReleases] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    api.get("/owner/users").then(({ data }) => setUsers((data || []).filter((u) => (u.wallet_balance || 0) > 0))).catch(() => setUsers([]));
    api.get("/staff/payout-release").then(({ data }) => setReleases(data)).catch(() => setReleases([]));
  };
  useEffect(() => { load(); }, []);

  const release = async (u) => {
    if (!u.paypal_email) { toast.error(`${u.display_name} has no PayPal email — they must complete /verify`); return; }
    const ok = window.confirm(`Release ${u.wallet_balance.toFixed(2)} NX to ${u.paypal_email}?`);
    if (!ok) return;
    setBusyId(u.id);
    try {
      await api.post("/staff/payout-release", { user_id: u.id });
      toast.success(`Released to ${u.paypal_email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Release failed"); }
    setBusyId(null);
  };

  return (
    <div className="space-y-6" data-testid="payouts-tab">
      <div>
        <div className="label-eyebrow mb-2 text-nexus-amber">// release wallet balance to paypal</div>
        <p className="text-xs text-white/50 max-w-2xl">Manually approve a payout for any user holding NX. Required when their account is suspended or KYC-pending — this clears suspension and converts NX → AUD at the current rate, then queues the PayPal transfer.</p>
      </div>

      <div className="space-y-px bg-white/5" data-testid="payouts-users-list">
        {users.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No users with positive NX balances.</div>}
        {users.map((u) => (
          <div key={u.id} className="bg-nexus-surface p-4 grid md:grid-cols-5 gap-3 items-center" data-testid={`payout-row-${u.id}`}>
            <div className="md:col-span-2 flex items-center gap-3">
              <img src={u.avatar} alt="" className="w-8 h-8 ring-1 ring-white/20" />
              <div className="min-w-0">
                <div className="text-sm truncate">{u.display_name} {u.suspended && <span className="text-[10px] text-nexus-crimson ml-2">SUSPENDED</span>}</div>
                <div className="text-[11px] text-white/40 truncate">{u.email} · {u.role}</div>
              </div>
            </div>
            <div className="text-xs text-white/60">
              <div className="label-eyebrow">paypal</div>
              <div className="mt-0.5 truncate">{u.paypal_email || <span className="text-nexus-crimson">not set</span>}</div>
            </div>
            <div className="text-right">
              <div className="label-eyebrow">balance</div>
              <div className="font-display text-xl text-nexus-amber mt-0.5">{(u.wallet_balance || 0).toFixed(2)}</div>
            </div>
            <div className="flex items-center justify-end gap-2">
              {!u.paypal_email && (
                <span className="text-[9px] tracking-[0.2em] uppercase font-display px-2 py-0.5 border border-nexus-crimson text-nexus-crimson" data-testid={`no-paypal-${u.id}`}>No PayPal</span>
              )}
              <button
                onClick={() => release(u)}
                disabled={busyId === u.id || !u.paypal_email}
                className={`text-[10px] tracking-[0.3em] uppercase font-display px-3 py-1.5 transition-colors ${
                  u.paypal_email
                    ? "btn-nexus"
                    : "border border-white/10 text-white/30 cursor-not-allowed bg-transparent"
                }`}
                data-testid={`release-${u.id}`}
              >
                {busyId === u.id ? "Releasing…" : "Release"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="label-eyebrow mb-3">// recent releases</div>
        <div className="space-y-px bg-white/5">
          {releases.length === 0 && <div className="bg-nexus-surface p-5 text-white/40 text-xs">No payouts released yet.</div>}
          {releases.slice(0, 20).map((r) => (
            <div key={r.id} className="bg-nexus-surface p-3 flex items-center gap-3 text-xs">
              <div className="flex-1 truncate">{r.user_name} → {r.paypal_email}</div>
              <div className="text-nexus-amber font-mono">{r.nx_amount.toFixed(2)} NX</div>
              <div className="text-nexus-cyan font-mono">${r.aud_amount.toFixed(2)} AUD</div>
              <div className="text-[10px] text-white/40">{new Date(r.ts).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

}
